import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, Copy, Edit2, X, Check, ChevronDown, ChevronUp, Percent, Calendar, Moon, Euro, Palette } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const RatesManager = ({ token }) => {
  const [rates, setRates] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRate, setEditingRate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedRates, setExpandedRates] = useState({});

  const emptyRate = {
    name_hr: '',
    name_en: '',
    name_de: '',
    name_it: '',
    short_desc_hr: '',
    short_desc_en: '',
    short_desc_de: '',
    short_desc_it: '',
    long_desc_hr: '',
    long_desc_en: '',
    long_desc_de: '',
    long_desc_it: '',
    min_nights: 1,
    max_nights: null,
    discount_percent: 0,
    fixed_surcharge: 0,
    start_date: '',
    end_date: '',
    color: '',
    room_ids: [],
    active: true,
    priority: 0
  };

  const [formData, setFormData] = useState(emptyRate);

  useEffect(() => {
    fetchRates();
    fetchRooms();
  }, []);

  const fetchRates = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRates(response.data);
    } catch (error) {
      console.error('Error fetching rates:', error);
      toast.error('Greška pri dohvaćanju rate-ova');
    } finally {
      setLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/rooms/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(response.data || []);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name_hr) {
      toast.error('Unesite ime rate-a (HR)');
      return;
    }
    
    if (formData.room_ids.length === 0) {
      toast.error('Odaberite barem jednu sobu');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        max_nights: formData.max_nights || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        color: formData.color || null
      };

      if (editingRate) {
        await axios.put(`${API}/api/admin/rates/${editingRate.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rate ažuriran');
      } else {
        await axios.post(`${API}/api/admin/rates`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Rate kreiran');
      }

      setShowForm(false);
      setEditingRate(null);
      setFormData(emptyRate);
      fetchRates();
    } catch (error) {
      console.error('Error saving rate:', error);
      toast.error('Greška pri spremanju rate-a');
    }
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm('Jeste li sigurni da želite obrisati ovaj rate?')) return;

    try {
      await axios.delete(`${API}/api/admin/rates/${rateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rate obrisan');
      fetchRates();
    } catch (error) {
      console.error('Error deleting rate:', error);
      toast.error('Greška pri brisanju rate-a');
    }
  };

  const handleClone = async (rate) => {
    // Pre-fill form with rate data for cloning
    setFormData({
      ...rate,
      room_ids: [] // Clear room IDs for new selection
    });
    setEditingRate(null);
    setShowForm(true);
    toast.info('Odaberite sobe za klonirani rate');
  };

  const handleEdit = (rate) => {
    setFormData({
      ...rate,
      max_nights: rate.max_nights || '',
      start_date: rate.start_date || '',
      end_date: rate.end_date || '',
      color: rate.color || ''
    });
    setEditingRate(rate);
    setShowForm(true);
  };

  const toggleRoomSelection = (roomId) => {
    setFormData(prev => ({
      ...prev,
      room_ids: prev.room_ids.includes(roomId)
        ? prev.room_ids.filter(id => id !== roomId)
        : [...prev.room_ids, roomId]
    }));
  };

  const selectAllRooms = () => {
    setFormData(prev => ({
      ...prev,
      room_ids: rooms.map(r => r.room_id)
    }));
  };

  const deselectAllRooms = () => {
    setFormData(prev => ({
      ...prev,
      room_ids: []
    }));
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.room_id === roomId);
    return room?.room_name || `Soba ${roomId}`;
  };

  const toggleExpand = (rateId) => {
    setExpandedRates(prev => ({
      ...prev,
      [rateId]: !prev[rateId]
    }));
  };

  if (loading) {
    return <div className="p-4 text-center">Učitavanje...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Percent className="w-5 h-5" />
            Room Rates / Tarife
          </h3>
          <p className="text-sm text-gray-500">
            Upravljajte cijenama i popustima za duže boravke
          </p>
        </div>
        <Button
          onClick={() => {
            setFormData(emptyRate);
            setEditingRate(null);
            setShowForm(true);
          }}
          className="bg-[var(--color-primary)]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Dodaj Rate
        </Button>
      </div>

      {/* Rate Form Modal */}
      {showForm && (
        <Card className="border-2 border-[var(--color-primary)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">
                {editingRate ? 'Uredi Rate' : 'Novi Rate'}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-gray-700">Ime Rate-a</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Hrvatski *</Label>
                      <Input
                        value={formData.name_hr}
                        onChange={(e) => setFormData({...formData, name_hr: e.target.value})}
                        placeholder="npr. Ostani duže, uštedi više!"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs">English</Label>
                      <Input
                        value={formData.name_en}
                        onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                        placeholder="e.g. Stay Longer, Save More!"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Deutsch</Label>
                      <Input
                        value={formData.name_de}
                        onChange={(e) => setFormData({...formData, name_de: e.target.value})}
                        placeholder="z.B. Länger bleiben, mehr sparen!"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Italiano</Label>
                      <Input
                        value={formData.name_it}
                        onChange={(e) => setFormData({...formData, name_it: e.target.value})}
                        placeholder="es. Resta più a lungo, risparmia!"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-gray-700">Kratki opis (za listu)</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Hrvatski</Label>
                      <Input
                        value={formData.short_desc_hr}
                        onChange={(e) => setFormData({...formData, short_desc_hr: e.target.value})}
                        placeholder="npr. 10% popusta za 4+ noći"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">English</Label>
                      <Input
                        value={formData.short_desc_en}
                        onChange={(e) => setFormData({...formData, short_desc_en: e.target.value})}
                        placeholder="e.g. 10% off for 4+ nights"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Deutsch</Label>
                      <Input
                        value={formData.short_desc_de}
                        onChange={(e) => setFormData({...formData, short_desc_de: e.target.value})}
                        placeholder="z.B. 10% Rabatt für 4+ Nächte"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Italiano</Label>
                      <Input
                        value={formData.short_desc_it}
                        onChange={(e) => setFormData({...formData, short_desc_it: e.target.value})}
                        placeholder="es. 10% di sconto per 4+ notti"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Long Description */}
              <div className="space-y-4">
                <h4 className="font-medium text-sm text-gray-700">Dugi opis (za stranicu rezervacije)</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Hrvatski</Label>
                    <Textarea
                      value={formData.long_desc_hr}
                      onChange={(e) => setFormData({...formData, long_desc_hr: e.target.value})}
                      placeholder="Detaljni opis rate-a..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">English</Label>
                    <Textarea
                      value={formData.long_desc_en}
                      onChange={(e) => setFormData({...formData, long_desc_en: e.target.value})}
                      placeholder="Detailed rate description..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Deutsch</Label>
                    <Textarea
                      value={formData.long_desc_de}
                      onChange={(e) => setFormData({...formData, long_desc_de: e.target.value})}
                      placeholder="Detaillierte Tarifbeschreibung..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Italiano</Label>
                    <Textarea
                      value={formData.long_desc_it}
                      onChange={(e) => setFormData({...formData, long_desc_it: e.target.value})}
                      placeholder="Descrizione dettagliata della tariffa..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Rate Configuration */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Moon className="w-3 h-3" /> Min. noćenja *
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.min_nights}
                    onChange={(e) => setFormData({...formData, min_nights: parseInt(e.target.value) || 1})}
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Moon className="w-3 h-3" /> Max. noćenja
                  </Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.max_nights || ''}
                    onChange={(e) => setFormData({...formData, max_nights: e.target.value ? parseInt(e.target.value) : null})}
                    placeholder="Neograničeno"
                  />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Percent className="w-3 h-3" /> Popust (%)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={formData.discount_percent}
                    onChange={(e) => setFormData({...formData, discount_percent: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Euro className="w-3 h-3" /> Nadoplata (€)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.fixed_surcharge}
                    onChange={(e) => setFormData({...formData, fixed_surcharge: parseFloat(e.target.value) || 0})}
                    placeholder="Po boravku"
                  />
                </div>
              </div>

              {/* Date Range (Optional) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Početak važenja
                  </Label>
                  <Input
                    type="date"
                    value={formData.start_date || ''}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  />
                  <p className="text-xs text-gray-400 mt-1">Ostavite prazno za cijelogodišnji rate</p>
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Kraj važenja
                  </Label>
                  <Input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>
              </div>

              {/* Color Picker for Promotional Rates */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <Label className="text-xs flex items-center gap-1 mb-2">
                      <Palette className="w-3 h-3" /> Boja promocije (opcionalno)
                    </Label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={formData.color || '#ec4899'}
                        onChange={(e) => setFormData({...formData, color: e.target.value})}
                        className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                        style={{ padding: '2px' }}
                      />
                      <Input
                        type="text"
                        value={formData.color || ''}
                        onChange={(e) => setFormData({...formData, color: e.target.value})}
                        placeholder="#ec4899"
                        className="w-32"
                      />
                      {formData.color && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({...formData, color: ''})}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Postavite boju za istaknuti prikaz promotivnih rate-ova
                    </p>
                  </div>
                  {formData.color && (
                    <div 
                      className="w-24 h-16 rounded-lg border-2 flex items-center justify-center text-white text-xs font-medium"
                      style={{ 
                        backgroundColor: formData.color,
                        borderColor: formData.color
                      }}
                    >
                      Preview
                    </div>
                  )}
                </div>
              </div>

              {/* Room Selection */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="font-medium">Sobe *</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllRooms}>
                      Odaberi sve
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={deselectAllRooms}>
                      Poništi sve
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-48 overflow-y-auto p-2 border rounded-lg">
                  {rooms.map((room) => (
                    <label
                      key={room.room_id}
                      className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                        formData.room_ids.includes(room.room_id)
                          ? 'bg-[var(--color-primary)] text-white'
                          : 'bg-gray-50 bg-[var(--color-primary-text-[var(--color-primary-hover)])]:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.room_ids.includes(room.room_id)}
                        onChange={() => toggleRoomSelection(room.room_id)}
                        className="sr-only"
                      />
                      <span className="text-sm truncate">{room.room_name || `Soba ${room.room_id}`}</span>
                    </label>
                  ))}
                </div>
                {formData.room_ids.length > 0 && (
                  <p className="text-sm text-gray-500">
                    Odabrano: {formData.room_ids.length} soba
                  </p>
                )}
              </div>

              {/* Priority & Active */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Prioritet:</Label>
                    <Input
                      type="number"
                      min="0"
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 0})}
                      className="w-20"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.active}
                      onCheckedChange={(checked) => setFormData({...formData, active: checked})}
                    />
                    <Label className="text-sm">Aktivan</Label>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Odustani
                  </Button>
                  <Button type="submit" className="bg-[var(--color-primary)]">
                    <Check className="w-4 h-4 mr-2" />
                    {editingRate ? 'Spremi promjene' : 'Kreiraj Rate'}
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Rates List */}
      <div className="space-y-3">
        {rates.length === 0 ? (
          <Card className="p-8 text-center text-gray-500">
            <p>Nema kreiranih rate-ova.</p>
            <p className="text-sm mt-2">Kliknite "Dodaj Rate" za kreiranje prvog rate-a.</p>
          </Card>
        ) : (
          rates.map((rate) => (
            <Card key={rate.id} className={`${!rate.active ? 'opacity-60' : ''}`}>
              <div 
                className="p-4 cursor-pointer bg-[var(--color-primary-text-[var(--color-primary-hover)])]:bg-gray-50 transition-colors"
                onClick={() => toggleExpand(rate.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {expandedRates[rate.id] ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                    <div>
                      <h4 className="font-semibold">{rate.name_hr || rate.name_en || 'Bez imena'}</h4>
                      <p className="text-sm text-gray-500">{rate.short_desc_hr || rate.short_desc_en}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={rate.discount_percent > 0 ? 'default' : 'secondary'}>
                        {rate.discount_percent > 0 ? `-${rate.discount_percent}%` : 'Bez popusta'}
                      </Badge>
                      <Badge variant="outline">
                        {rate.min_nights}{rate.max_nights ? `-${rate.max_nights}` : '+'} noći
                      </Badge>
                      <Badge variant="outline" className="text-gray-500">
                        {rate.room_ids.length} soba
                      </Badge>
                      {rate.color && (
                        <div 
                          className="w-5 h-5 rounded-full border-2 border-white shadow-sm"
                          style={{ backgroundColor: rate.color }}
                          title={`Boja: ${rate.color}`}
                        />
                      )}
                      {!rate.active && (
                        <Badge variant="destructive">Neaktivan</Badge>
                      )}
                    </div>
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(rate)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleClone(rate)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(rate.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {expandedRates[rate.id] && (
                <div className="px-4 pb-4 pt-2 border-t bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Popust:</span>
                      <span className="ml-2 font-medium">{rate.discount_percent}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Nadoplata:</span>
                      <span className="ml-2 font-medium">€{rate.fixed_surcharge || 0}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Period:</span>
                      <span className="ml-2 font-medium">
                        {rate.start_date && rate.end_date 
                          ? `${rate.start_date} - ${rate.end_date}`
                          : 'Cijelogodišnji'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Prioritet:</span>
                      <span className="ml-2 font-medium">{rate.priority}</span>
                    </div>
                    {rate.color && (
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">Boja:</span>
                        <div 
                          className="w-6 h-6 rounded border"
                          style={{ backgroundColor: rate.color }}
                        />
                        <span className="font-medium text-xs">{rate.color}</span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3">
                    <span className="text-gray-500 text-sm">Sobe:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {rate.room_ids.map((roomId) => (
                        <Badge key={roomId} variant="outline" className="text-xs">
                          {getRoomName(roomId)}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {rate.long_desc_hr && (
                    <div className="mt-3">
                      <span className="text-gray-500 text-sm">Dugi opis:</span>
                      <p className="text-sm mt-1">{rate.long_desc_hr}</p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default RatesManager;
